import React, { useContext, useEffect, useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { Context } from '../index';
import { updateProfile } from '../http/userAPI';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import '../styles/Profile.css';
import UserDiscountInfo from '../components/UserDiscountInfo';
import OrderFormConsent from '../components/OrderFormConsent';
import { FaBoxOpen, FaShoppingCart, FaThList, FaSignOutAlt } from 'react-icons/fa';
import Barcode from 'react-barcode';
import { changePassword } from '../http/userAPI';

const Profile = observer(() => {
    const { user } = useContext(Context);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        phone: '',
    });
    const [consentChecked, setConsentChecked] = useState(false);
    const [marketingChecked, setMarketingChecked] = useState(false);
    const [consentSaved, setConsentSaved] = useState(false);
    const [loadingUser, setLoadingUser] = useState(true);
    const navigate = useNavigate();
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    useEffect(() => {
        if (!user.isAuth) {
            navigate('/login');
            return;
        }
        if (!user.isRefreshing) {
            user.refreshUser();
        }
    }, [user.isAuth, navigate, user]);

    useEffect(() => {
        const fetchUser = async () => {
            await user.refreshUser();
            setLoadingUser(false);
        };
        fetchUser();
    }, []);

    useEffect(() => {
        if (user.user) {
            setFormData({
                name: user.user.name || '',
                surname: user.user.surname || '',
                email: user.user.email || '',
                phone: user.user.phone || '',
            });
            setConsentChecked(!!user.user.consent_personal_data);
            setMarketingChecked(!!user.user.consent_marketing);
        }
    }, [user.user]);

    useEffect(() => {
        if (!user.user || !user.user.name) {
            const savedProfile = localStorage.getItem('profileData');
            if (savedProfile) {
                const parsed = JSON.parse(savedProfile);
                setFormData(parsed.formData || {});
            }
        }
    }, [user.user]);

    useEffect(() => {
        if (!user.user || !user.user.name) {
            localStorage.setItem('profileData', JSON.stringify({
                formData,
            }));
        }
    }, [formData, user.user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleConsentChange = (e) => {
        const { name, checked } = e.target;
        if (name === 'consent_personal_data') setConsentChecked(checked);
        if (name === 'consent_marketing') setMarketingChecked(checked);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            await updateProfile(formData);
            await user.refreshUser();
            setTimeout(() => {
                setFormData({
                    name: user.user.name || '',
                    surname: user.user.surname || '',
                    email: user.user.email || '',
                    phone: user.user.phone || '',
                });
            }, 200);
            setSuccess('Профиль успешно обновлен');
            localStorage.removeItem('profileData');
        } catch (e) {
            setError(e?.response?.data?.message || 'Ошибка при обновлении профиля');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConsent = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            await updateProfile({
                consent_personal_data: consentChecked,
                consent_marketing: marketingChecked
            });
            await user.refreshUser();
            setConsentSaved(true);
            setSuccess('Согласие сохранено!');
            localStorage.removeItem('profileData');
        } catch (e) {
            setError(e?.response?.data?.message || 'Ошибка при сохранении согласия');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');
        if (newPassword.length < 6) {
            setPasswordError('Новый пароль должен содержать минимум 6 символов');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('Пароли не совпадают');
            return;
        }
        setPasswordLoading(true);
        try {
            await changePassword(oldPassword, newPassword);
            setPasswordSuccess('Пароль успешно изменён');
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordForm(false);
        } catch (e) {
            setPasswordError(e?.response?.data?.message || 'Ошибка при смене пароля');
        } finally {
            setPasswordLoading(false);
        }
    };

    if (loadingUser) {
        return <div style={{padding: 32}}>Загрузка профиля...</div>;
    }

    if (!user.isAuth) {
        return null;
    }

    return (
        <Container className="profile-page mt-4">
            <h1 className="profile-title">Личный кабинет</h1>
            {/* Предупреждение о неподтвержденном email */}
            {user.user && user.user.is_verified === false && (
                <Alert variant="warning" className="mb-4">
                    Ваш email не подтвержден! Пожалуйста, проверьте почту и перейдите по ссылке для активации аккаунта.
                </Alert>
            )}
            {/* Блок с отображением barcode */}
            {user.user && user.user.barcode && (
                <Card className="mb-4 barcode-card">
                    <Card.Body className="text-center">
                        <div style={{marginBottom: 8, fontWeight: 500}}>Ваш персональный штрих-код для скидки:</div>
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                            <Barcode value={user.user.barcode} width={2} height={80} fontSize={16} displayValue={true} background="#fff" lineColor="#000" />
                        </div>
                        <div style={{fontSize: 13, color: '#888', marginTop: 8}}>
                            Покажите этот штрих-код продавцу для применения персональной скидки
                        </div>
                    </Card.Body>
                </Card>
            )}
            <Row>
                <Col lg={8}>
                    <Card className="profile-card">
                        <Card.Body>
                            <h2 className="profile-section-title">Профиль</h2>
                            {error && <Alert variant="danger">{error}</Alert>}
                            {success && <Alert variant="success">{success}</Alert>}
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Имя</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder="Введите ваше имя"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Фамилия</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="surname"
                                                value={formData.surname}
                                                onChange={handleInputChange}
                                                placeholder="Введите вашу фамилию"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Введите ваш email"
                                        disabled
                                    />
                                </Form.Group>
                                <Button 
                                    variant="primary" 
                                    type="submit"
                                    className="save-profile-button"
                                    disabled={loading}
                                >
                                    {loading ? 'Сохранение...' : 'Сохранить изменения'}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                    <Card className="mt-4">
                        <Card.Body>
                            <h2 className="profile-section-title">Согласие на обработку персональных данных</h2>
                            <Form>
                                <Form.Check
                                    type="checkbox"
                                    name="consent_personal_data"
                                    checked={consentChecked}
                                    onChange={handleConsentChange}
                                    label="Я соглашаюсь на обработку моих персональных данных (ФИО, mail) для оформления и доставки заказа, а также для получения информации о статусе покупки. С Политикой конфиденциальности ознакомлен(а)."
                                />
                                <Form.Check
                                    type="checkbox"
                                    name="consent_marketing"
                                    checked={marketingChecked}
                                    onChange={handleConsentChange}
                                    label="Согласен(а) получать информацию о новинках и акциях на указанные контакты."
                                />
                                <Button
                                    variant="primary"
                                    onClick={handleSaveConsent}
                                    disabled={!consentChecked || loading}
                                    className="mt-2"
                                >
                                    {loading ? 'Сохранение...' : 'Сохранить согласие'}
                                </Button>
                                {consentSaved && (
                                    <Alert variant="success" className="mt-3">
                                        Согласие сохранено!
                                    </Alert>
                                )}
                            </Form>
                        </Card.Body>
                    </Card>
                    <Card className="mt-4">
                        <Card.Body>
                            <h2 className="profile-section-title">Смена пароля</h2>
                            <Button variant="outline-primary" className="mb-3" onClick={() => setShowPasswordForm(v => !v)}>
                                {showPasswordForm ? 'Скрыть форму' : 'Сменить пароль'}
                            </Button>
                            {showPasswordForm && (
                                <Form onSubmit={handlePasswordChange} className="change-password-form">
                                    <Form.Group className="mb-2">
                                        <Form.Label>Старый пароль</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={oldPassword}
                                            onChange={e => setOldPassword(e.target.value)}
                                            placeholder="Введите старый пароль"
                                            required
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Новый пароль</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            placeholder="Введите новый пароль"
                                            required
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Подтверждение нового пароля</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            placeholder="Повторите новый пароль"
                                            required
                                        />
                                    </Form.Group>
                                    <Button type="submit" variant="primary" disabled={passwordLoading}>
                                        {passwordLoading ? 'Сохранение...' : 'Сменить пароль'}
                                    </Button>
                                    {passwordError && <Alert variant="danger" className="mt-2">{passwordError}</Alert>}
                                    {passwordSuccess && <Alert variant="success" className="mt-2">{passwordSuccess}</Alert>}
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="profile-menu-card">
                        <Card.Body>
                            <h2 className="profile-section-title">Меню</h2>
                            <div className="profile-menu">
                                <Button 
                                    variant="outline-primary"
                                    className="profile-menu-button"
                                    onClick={() => navigate('/orders')}
                                >
                                    <FaBoxOpen style={{marginRight:8, marginBottom:2}}/>
                                    Мои заказы
                                </Button>
                                <Button 
                                    variant="outline-primary"
                                    className="profile-menu-button"
                                    onClick={() => navigate('/basket')}
                                >
                                    <FaShoppingCart style={{marginRight:8, marginBottom:2}}/>
                                    Корзина
                                </Button>
                                <Button 
                                    variant="outline-primary"
                                    className="profile-menu-button"
                                    onClick={() => navigate('/shop')}
                                >
                                    <FaThList style={{marginRight:8, marginBottom:2}}/>
                                    Каталог товаров
                                </Button>
                                <Button 
                                    variant="outline-danger"
                                    className="profile-menu-button"
                                    onClick={() => {
                                        user.logout();
                                        navigate('/');
                                    }}
                                >
                                    <FaSignOutAlt style={{marginRight:8, marginBottom:2}}/>
                                    Выйти
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                    <UserDiscountInfo />
                </Col>
            </Row>
        </Container>
    );
});

export default Profile; 